import { supabase } from "@/services/supabase";
import { Json } from "@/types/supabase";
import {
  CanvasDocumentInsert,
  CanvasDocumentUpdate,
  CanvasDocumentVersion,
  CanvasDocumentVersionInsert,
} from "@/types/supabase.ext";
import { CanvasDocumentContent, CanvasDocumentWithContent } from "./types";
import { CanvasDocumentContentV2, CANVAS_SCHEMA_VERSION_V2 } from "./types/schema";
import { normalizeV2Content } from "./utils/normalize";

// ================================================================
// CANVAS DOCUMENT OPERATIONS
// ================================================================

export async function getCanvasDocument(documentId: string): Promise<CanvasDocumentWithContent> {
  const { data, error } = await supabase
    .from("canvas_documents")
    .select("*")
    .eq("id", documentId)
    .single();

  if (error) {
    console.error("Error fetching canvas document:", error);
    throw new Error(`Failed to fetch canvas document: ${error.message}`);
  }

  return {
    ...data,
    content: data.content as unknown as CanvasDocumentContent,
  };
}

export async function getProjectDocuments(projectId: string): Promise<CanvasDocumentWithContent[]> {
  const { data, error } = await supabase
    .from("canvas_documents")
    .select("*")
    .eq("project_id", projectId)
    .order("updated_at", { ascending: false });

  if (error) {
    console.error("Error fetching project documents:", error);
    throw new Error(`Failed to fetch project documents: ${error.message}`);
  }

  return (data || []).map((doc) => ({
    ...doc,
    content: doc.content as unknown as CanvasDocumentContent,
  }));
}

export async function getMainDocument(
  projectId: string,
): Promise<CanvasDocumentWithContent | null> {
  const { data, error } = await supabase
    .from("canvas_documents")
    .select("*")
    .eq("project_id", projectId)
    .eq("report_type", "main")
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      // No rows returned - no main document exists
      return null;
    }
    console.error("Error fetching main document:", error);
    throw new Error(`Failed to fetch main document: ${error.message}`);
  }

  return {
    ...data,
    content: data.content as unknown as CanvasDocumentContent,
  };
}

export async function createCanvasDocument(
  document: CanvasDocumentInsert,
): Promise<CanvasDocumentWithContent> {
  const { data, error } = await supabase
    .from("canvas_documents")
    .insert(document)
    .select()
    .single();

  if (error) {
    console.error("Error creating canvas document:", error);
    throw new Error(`Failed to create canvas document: ${error.message}`);
  }

  return {
    ...data,
    content: data.content as unknown as CanvasDocumentContent,
  };
}

export async function updateCanvasDocument(
  documentId: string,
  updates: CanvasDocumentUpdate,
): Promise<CanvasDocumentWithContent> {
  const { data, error } = await supabase
    .from("canvas_documents")
    .update(updates)
    .eq("id", documentId)
    .select()
    .single();

  if (error) {
    console.error("Error updating canvas document:", error);
    throw new Error(`Failed to update canvas document: ${error.message}`);
  }

  return {
    ...data,
    content: data.content as unknown as CanvasDocumentContent,
  };
}

export async function deleteCanvasDocument(documentId: string): Promise<void> {
  const { error } = await supabase.from("canvas_documents").delete().eq("id", documentId);

  if (error) {
    console.error("Error deleting canvas document:", error);
    throw new Error(`Failed to delete canvas document: ${error.message}`);
  }
}

// ================================================================
// VERSION OPERATIONS
// ================================================================

export async function getDocumentVersions(documentId: string): Promise<CanvasDocumentVersion[]> {
  const { data, error } = await supabase
    .from("canvas_document_versions")
    .select("*")
    .eq("document_id", documentId)
    .order("version_number", { ascending: false });

  if (error) {
    console.error("Error fetching document versions:", error);
    throw new Error(`Failed to fetch document versions: ${error.message}`);
  }

  return data || [];
}

export async function getDocumentVersionContent(versionId: string): Promise<CanvasDocumentContent> {
  const { data, error } = await supabase
    .from("canvas_document_versions")
    .select("content")
    .eq("id", versionId)
    .single();

  if (error) {
    console.error("Error fetching version content:", error);
    throw new Error(`Failed to fetch version content: ${error.message}`);
  }

  return data.content as unknown as CanvasDocumentContent;
}

export async function createDocumentVersion(
  documentId: string,
  content: CanvasDocumentContent,
  createdBy?: string,
): Promise<CanvasDocumentVersion> {
  // Get next version number using database function
  const { data: nextVersion, error: versionError } = await supabase.rpc(
    "get_next_canvas_document_version",
    { p_document_id: documentId },
  );

  if (versionError) {
    console.error("Error getting next version number:", versionError);
    throw new Error(`Failed to get next version number: ${versionError.message}`);
  }

  const versionInsert: CanvasDocumentVersionInsert = {
    document_id: documentId,
    version_number: nextVersion,
    content: content as unknown as Json,
    created_by: createdBy,
  };

  const { data, error } = await supabase
    .from("canvas_document_versions")
    .insert(versionInsert)
    .select()
    .single();

  if (error) {
    console.error("Error creating document version:", error);
    throw new Error(`Failed to create document version: ${error.message}`);
  }

  return data;
}

// ================================================================
// COMBINED OPERATIONS
// ================================================================

/**
 * Save document content and create a version snapshot.
 * This is the primary save operation that should be used by autosave.
 */
export async function saveDocumentWithVersion(
  documentId: string,
  content: CanvasDocumentContent,
  createdBy?: string,
): Promise<{ document: CanvasDocumentWithContent; version: CanvasDocumentVersion }> {
  // Update the document
  const document = await updateCanvasDocument(documentId, {
    content: content as unknown as Json,
  });

  // Create a version snapshot
  const version = await createDocumentVersion(documentId, content, createdBy);

  return { document, version };
}

/**
 * Normalize a document's V2 content and save if changes were made.
 * This is called automatically when loading documents to fix inconsistent formats.
 *
 * @returns The normalized document, or null if no normalization was needed
 */
export async function normalizeAndSaveIfNeeded(
  document: CanvasDocumentWithContent,
  createdBy?: string,
): Promise<CanvasDocumentWithContent | null> {
  const content = document.content as unknown as CanvasDocumentContentV2;

  if (content.schemaVersion !== CANVAS_SCHEMA_VERSION_V2) {
    console.error(`[canvas/service] Unexpected schemaVersion: ${content.schemaVersion}`);
    return null;
  }

  const { content: normalizedContent, wasNormalized } = normalizeV2Content(content);

  if (!wasNormalized) {
    return null;
  }

  console.log(
    `[canvas/service] Normalizing document ${document.id} - fixing inconsistent block formats`,
  );

  // Save the normalized content (this also creates a version)
  const { document: savedDocument } = await saveDocumentWithVersion(
    document.id,
    normalizedContent as unknown as CanvasDocumentContent,
    createdBy,
  );

  return savedDocument;
}

/**
 * Create a new document as the "main" report for a project.
 * Checks that no main report already exists.
 */
export async function createMainDocument(
  projectId: string,
  title: string,
  createdBy?: string,
  initialContent?: CanvasDocumentContent,
): Promise<CanvasDocumentWithContent> {
  // Check if main already exists
  const existing = await getMainDocument(projectId);
  if (existing) {
    throw new Error("A main report already exists for this project");
  }

  return createCanvasDocument({
    project_id: projectId,
    title,
    report_type: "main",
    content: initialContent as unknown as Json,
    created_by: createdBy,
  });
}
